import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPosMessageForm } from '../hooks/useUpsertPosMessageForm';
import type {
	PosMessageDto,
	UpsertPosMessageDto,
} from '../types/PosMessageDto';
import { UpsertPosMessageForm } from './UpsertPosMessageForm';

interface OrganisationEditorModal {
	close: () => void;
	opened: boolean;
	updatePosMessageRecord: (posmessage: PosMessageDto) => void;
	posMessage: UpsertPosMessageDto;
}

export function EditPosMessageModal({
	opened,
	close,
	updatePosMessageRecord,
	posMessage,
}: OrganisationEditorModal) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPosMessageForm(posMessage);

	const editPosMessageMutation = mutate<UpsertPosMessageDto, PosMessageDto>(
		`${organisation}/pos-messages/edit`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (editPosMessageMutation.data?.value) {
			updatePosMessageRecord(editPosMessageMutation.data.value);
			form.setInitialValues(inputs);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'Edit POS Message'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editPosMessageMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'POS Message Edited!'}
					body={
						'A new POS message has been successfully added. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editPosMessageMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertPosMessageForm form={form} inputs={inputs} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
